import { STATUS_PRECONDITION_REQUIRED } from 'src/axios/axios.constants';
import { SignUpService } from 'src/pages/Auth/SignUp/SignUp.service';

/*
 * BUG-800 — an invited teammate who has not activated yet and comes in by a
 * door other than the emailed link (Google, a typed password, the password
 * reset, sign-up). The backend refuses those doors on purpose and says why in
 * `data.reason`, in the body of bff-web's 428 and of workspace-service's own
 * 422 and 410. Everything here reads that one field.
 */

/** Why a door refuses an invited address. */
export type tInvitationReason = 'pending_invitation' | 'invitation_expired';

/** The copy block of each reason, under `page.invitation` of the page's namespace. */
export const INVITATION_COPY_KEY: Record<
  tInvitationReason,
  'pending' | 'expired'
> = {
  pending_invitation: 'pending',
  invitation_expired: 'expired',
};

/** The 428 of `/auth/sign-in` and `/auth/sign-up`. */
export type tInvitationRefusal = {
  reason: tInvitationReason;
  identityAcceptAllowed: boolean;
};

/**
 * What a sign-in or sign-up door answers for an invited address: the Google
 * identity is enough to accept the invitation without a password, or the
 * person has to be told what to do next, with the address they used.
 */
export type tInvitationDoorOutcome =
  | { kind: 'identityAccept' }
  | { kind: 'notice'; reason: tInvitationReason; email: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readResponse(
  error: unknown,
): { status?: number; data?: Record<string, unknown> } | undefined {
  if (!isRecord(error) || !isRecord(error.response)) return undefined;
  const { status, data } = error.response;
  return {
    status: typeof status === 'number' ? status : undefined,
    data: isRecord(data) && isRecord(data.data) ? data.data : undefined,
  };
}

export function isInvitationReason(value: unknown): value is tInvitationReason {
  return value === 'pending_invitation' || value === 'invitation_expired';
}

/** `data.reason` of a refused request, whatever its status. */
export function readRefusalReason(error: unknown): string | undefined {
  const reason = readResponse(error)?.data?.reason;
  return typeof reason === 'string' ? reason : undefined;
}

export function readInvitationRefusal(
  error: unknown,
): tInvitationRefusal | null {
  const response = readResponse(error);
  const reason = response?.data?.reason;
  if (
    response?.status !== STATUS_PRECONDITION_REQUIRED ||
    !isInvitationReason(reason)
  ) {
    return null;
  }
  return {
    reason,
    identityAcceptAllowed: response.data?.identityAcceptAllowed === true,
  };
}

export function toInvitationDoorOutcome(
  refusal: tInvitationRefusal,
  email: string,
): tInvitationDoorOutcome {
  if (refusal.identityAcceptAllowed) {
    return { kind: 'identityAccept' };
  }
  return { kind: 'notice', reason: refusal.reason, email };
}

/** After this, a door shows what it shows today: its answer cannot wait longer. */
const EMAIL_CHECK_TIMEOUT_MS = 4000;

/**
 * A rejected password sign-in and a password-reset request are answered by
 * Firebase alone, and an invitee has no account there. The anonymous email
 * check of the sign-up page knows whether the typed address is invited, and
 * asking it re-sends a live invitation like any other door does.
 *
 * Never throws: when the check cannot answer, in time or at all, the door
 * shows what it shows today.
 */
export async function findInvitationByEmail(
  email: string,
): Promise<tInvitationReason | null> {
  try {
    await SignUpService.verifyEmailExists(email, {
      // A registered address answers 422 here, so most wrong passwords would
      // otherwise become a Sentry event with the address in its endpoint.
      skipSentryCapture: true,
      // An anonymous check: its 401 says nothing about anybody's session.
      skipAutoRedirect: true,
      // The typed address is the last segment of the path.
      redactLog: true,
      // The page waits for this answer before it shows anything.
      timeout: EMAIL_CHECK_TIMEOUT_MS,
    });
    return null;
  } catch (error) {
    const reason = readRefusalReason(error);
    return isInvitationReason(reason) ? reason : null;
  }
}
